<template>
    <label
        :class="size === 'md' ? 'h-16 w-full' : 'h-12 md:min-w-36 md:max-w-40'"
        class="flex w-full items-center overflow-hidden rounded-full border border-ash-200">
        <button
            type="button"
            aria-label="уменьшить"
            :disabled="normalizedModelValue <= minValue"
            @click="decrease"
            :class="[
                size === 'md' ? 'py-[1.375rem]' : 'py-3.5',
                size === 'sm' ? 'pl-2.5 pr-1.5 md:pl-5' : 'pl-5 pr-1.5',
            ]"
            class="outline-none transition-opacity duration-200 enabled:hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-20">
            <AtomIcon
                name="minus"
                class="size-5" />
        </button>

        <input
            name="counter"
            :value="inputValue"
            type="number"
            inputmode="numeric"
            :min="minValue"
            :max="maxValue"
            :class="size === 'md' ? 'py-[1.375rem]' : 'py-3.5'"
            class="w-full bg-transparent text-center leading-none outline-none [-moz-appearance:textfield] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            @input="onInput"
            @blur="onBlur" />

        <button
            type="button"
            aria-label="увеличить"
            :disabled="normalizedModelValue >= maxValue"
            @click="increase"
            :class="[
                size === 'md' ? 'py-[1.375rem]' : 'py-3.5',
                size === 'sm' ? 'pl-1.5 pr-2.5 md:pr-5' : 'pl-1.5 pr-5',
            ]"
            class="transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-40">
            <AtomIcon
                name="plus"
                class="size-5" />
        </button>
    </label>
</template>

<script setup lang="ts">
import { type PropType, computed, ref, watch } from 'vue'

import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'

interface Props {
    min?: number | string
    max?: number | string
    size?: 'sm' | 'md'
}

const props = withDefaults(defineProps<Props>(), {
    min: 1,
    max: Number.MAX_SAFE_INTEGER,
    size: 'sm',
})

const emit = defineEmits<{
    change: [value: number]
}>()

const model = defineModel({
    type: [String, Number] as PropType<string | number>,
    required: true,
})

const minValue = computed(() => Number(props.min ?? 0))
const maxValue = computed(() => Number(props.max ?? Number.MAX_SAFE_INTEGER))

const normalizeValue = (value: string | number): number => {
    const parsed = Number(value)

    if (!Number.isFinite(parsed)) return minValue.value

    return Math.min(
        Math.max(Math.round(parsed), minValue.value),
        maxValue.value
    )
}

const normalizedModelValue = computed(() => normalizeValue(model.value))
const inputValue = ref(String(normalizedModelValue.value))

const updateValue = (value: string | number) => {
    const normalized = normalizeValue(value)
    model.value = normalized
    inputValue.value = String(normalized)
    emit('change', normalized)
}

watch(
    normalizedModelValue,
    (value) => {
        if (inputValue.value !== String(value)) {
            inputValue.value = String(value)
        }
    },
    { immediate: true }
)

const onInput = (event: Event) => {
    const target = event.target as HTMLInputElement
    inputValue.value = target.value
}

const onBlur = (event: Event) => {
    const target = event.target as HTMLInputElement
    const normalized = normalizeValue(target.value)

    target.value = String(normalized)
    model.value = normalized
    inputValue.value = String(normalized)
    emit('change', normalized)
}

const increase = () => {
    updateValue(normalizedModelValue.value + 1)
}

const decrease = () => {
    updateValue(normalizedModelValue.value - 1)
}
</script>
